import { Button, Card, InfoTip, Input } from "@app/ui";
import { useEffect, useState } from "react";
import { useI18n } from "../../lib/useI18n.js";
import { useAuthStore, useSyncStore } from "../../stores/index.js";

/** Multi-device sync. The server URL is baked into the hosted build (so users
 *  just sign in via the Account card); it's only surfaced under "Advanced" for
 *  self-hosters. The URL + auth tokens are device-only and never backed up. */
export function SyncCard() {
  const { locale, t } = useI18n();
  const url = useSyncStore((s) => s.url);
  const lastSyncAt = useSyncStore((s) => s.lastSyncAt);
  const busy = useSyncStore((s) => s.busy);
  const phase = useSyncStore((s) => s.phase);
  const error = useSyncStore((s) => s.error);
  const loaded = useSyncStore((s) => s.loaded);
  const load = useSyncStore((s) => s.load);
  const setUrl = useSyncStore((s) => s.setUrl);
  const syncNow = useSyncStore((s) => s.syncNow);
  const signedIn = Boolean(useAuthStore((s) => s.accessToken));
  const [urlInput, setUrlInput] = useState("");

  // Tick an elapsed-seconds counter while a sync runs, so a slow run (esp. a
  // cold-start wake) visibly shows it's alive and how long it's been working.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) return;
    const start = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);
  useEffect(() => {
    setUrlInput(url);
  }, [url]);

  const dirty = urlInput !== url;

  return (
    <Card className="p-4">
      <h2 className="mb-3 flex items-center gap-1.5 font-medium">
        {t("sync.multiDevice")}
        <InfoTip label={t("common.moreInfo")}>{t("sync.multiDevice.description")}</InfoTip>
      </h2>
      <div className="flex items-center gap-2">
        <Button disabled={busy || !url || !signedIn} onClick={() => void syncNow()}>
          {busy ? t("sync.syncing") : t("sync.syncNow")}
        </Button>
        <span className="text-xs text-muted-foreground">
          {busy
            ? `${phase ?? t("sync.syncing")}${elapsed > 0 ? ` (${elapsed}s)` : ""}`
            : error
              ? `Error: ${error}`
              : !signedIn
                ? t("sync.signInToSync")
                : !url
                  ? t("sync.setServer")
                  : lastSyncAt
                    ? t("sync.lastSynced", { time: new Date(lastSyncAt).toLocaleString(locale) })
                    : t("sync.enabled")}
        </span>
      </div>

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          {t("sync.advancedServer")}
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">
            {t("sync.serverDescription")}
          </p>
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://your-sync-server"
          />
          <Button
            variant="outline"
            className="self-start"
            disabled={busy || !dirty}
            onClick={() => void setUrl(urlInput)}
          >
            {t("sync.saveServer")}
          </Button>
        </div>
      </details>
    </Card>
  );
}
